import { ToastService } from "@shared/lib/services";
import { BackednResponse } from "@shared/lib/types";
import {
  GetAccounting,
  IGetAccountingParams,
} from "@shared/lib/types/accounting";
import { InfiniteData, useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { INVOICES_QUERY_KEY } from "../constants";
import { AccountingService } from "../services/Accounting.service";

export function useGetInvoices(params: IGetAccountingParams) {
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<
    BackednResponse<GetAccounting>,
    Error,
    InfiniteData<BackednResponse<GetAccounting>>,
    (string | IGetAccountingParams)[],
    number
  >({
    queryKey: [...INVOICES_QUERY_KEY, params],
    queryFn: ({ pageParam = 1 }) =>
      AccountingService.getBrandInvoices({
        ...params,
        pageNo: params.pageNo || (pageParam as number),
      }),
    getNextPageParam: (lastPage) => {
      if (lastPage?.data?.pagination?.hasNextPage) {
        return lastPage.data.pagination.currentPage + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
  });

  const errorMessage = isError && error?.message;

  useEffect(() => {
    if (errorMessage) {
      ToastService.error(errorMessage);
    }
  }, [errorMessage]);

  const flattened = useMemo(() => {
    if (!data?.pages) return undefined;
    return {
      data: data.pages.flatMap(
        (page: BackednResponse<GetAccounting>) => page?.data?.data ?? []
      ),
      pagination: data.pages?.[data.pages?.length - 1]?.data?.pagination,
    };
  }, [data]);

  return {
    invoices: flattened?.data,
    pagination: flattened?.pagination,
    isFetching: isLoading || isFetchingNextPage,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
  };
}
